import { http } from '@/api/http'
import type {
  BranchAssignment,
  ProfileAccess,
  ProfileCustomsPost,
  ProfileStatus,
  UserIdentity,
  UsersTab,
  UserStatus,
  UserWarehouse,
} from '@/types/api'
import type { RequestOptions } from '../crud'
import { createCrudService } from '../crud'

/**
 * A user is created under a role (its sub-tab); the backend takes the client
 * type — and so the mobile app — from the role, so none is sent.
 */
export type CreateUserInput = {
  role_id: string
  /** ФИО. When a name part is sent the backend builds it from the parts in the company's name format. */
  display_name: string
  first_name?: string
  last_name?: string
  middle_name?: string
  login: string
  phone: string
  password: string
  /** Staff: exactly one default branch; Администратор and Контрагент: none. */
  branches: BranchAssignment[]
  avatar_url?: string
  locale?: string
  /** Only for roles listing it in `extra_fields`. UZS decimal string. */
  agent_limit?: string
  cashbox_id?: string
  partner_id?: string
}

/** Profile fields only (omitted = unchanged). Role, status, branches and password have dedicated commands. */
export type UpdateUserInput = Partial<
  Pick<
    CreateUserInput,
    'login' | 'phone' | 'display_name' | 'first_name' | 'last_name' | 'middle_name' | 'avatar_url' | 'locale' | 'agent_limit' | 'cashbox_id' | 'partner_id'
  >
>

const crud = createCrudService<UserIdentity, CreateUserInput, UpdateUserInput>('users')
const key = (id: string) => `/users/${encodeURIComponent(id)}`

export const usersService = {
  ...crud,

  /**
   * Saves the profile fields and, as Ucode's edit form did, a new password
   * typed into the same form (empty keeps the current one). The password
   * goes through its own command, which also ends the user's sessions.
   */
  async update(id: string, body: UpdateUserInput & { new_password?: string; branch_id?: string }) {
    const { new_password: password, branch_id: branchId, ...profile } = body
    let updated: UserIdentity | null = null
    if (Object.keys(profile).length > 0) updated = await crud.update(id, profile)
    if (password) await usersService.resetPassword(id, password)
    // Ucode's single «Филиал»: a changed branch becomes the one default branch.
    if (branchId) await usersService.assignBranches(id, [{ branch_id: branchId, is_default: true }])
    return updated && !password && !branchId ? updated : crud.get(id)
  },

  /** Tabs (top client types) and sub-tabs (their active roles) of «Пользователи по ролям». */
  async tabs(options?: RequestOptions) {
    const { data } = await http.get<UsersTab[]>('/users/tabs', options)
    return data
  },

  /** Up to 200 at once, all or none (409 names the user that blocks it). */
  async bulkRemove(ids: string[]) {
    await http.post('/users/bulk-delete', { ids })
  },

  async warehouses(id: string, options?: RequestOptions) {
    const { data } = await http.get<UserWarehouse[]>(`${key(id)}/warehouses`, options)
    return data
  },

  /** The complete list: warehouses left out are unassigned. */
  async setWarehouses(id: string, warehouseIds: string[]) {
    const { data } = await http.put<UserWarehouse[]>(`${key(id)}/warehouses`, { warehouse_ids: warehouseIds })
    return data
  },

  /** Customs posts the user works at (limit what they see without the all-posts right). */
  async customsPosts(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProfileCustomsPost[]>(`${key(id)}/customs-posts`, options)
    return data ?? []
  },

  /** The complete list: posts left out are unassigned. */
  async setCustomsPosts(id: string, locationIds: string[]) {
    const { data } = await http.put<ProfileCustomsPost[]>(`${key(id)}/customs-posts`, { location_ids: locationIds })
    return data ?? []
  },

  /** The profile's effective access: role, branches and allowed permissions. */
  async access(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProfileAccess>(`${key(id)}/access`, options)
    return data
  },

  async assignRole(id: string, roleId: string) {
    const { data } = await http.put<UserIdentity>(`${key(id)}/role`, { role_id: roleId })
    return data
  },

  async assignBranches(id: string, branches: BranchAssignment[]) {
    const { data } = await http.put<UserIdentity>(`${key(id)}/branches`, { branches })
    return data
  },

  async changeStatus(id: string, status: { user_status?: UserStatus; profile_status?: ProfileStatus }) {
    const { data } = await http.put<UserIdentity>(`${key(id)}/status`, status)
    return data
  },

  async resetPassword(id: string, password: string) {
    await http.put(`${key(id)}/password`, { password })
  },
}
